import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const initialForm = {
  name: '',
  description: '',
  price: '',
  stock: '',
  category: '',
  variety: '',
  packQuantity: '',
  volume: '',
  alcoholPercentage: '',
  countryCode: '',
  barcode: '',
  weight: '',
  pallet: '',
  image: null
};

function AdminProductForm({
  className = '',
  onProductAdded = () => {},
  editingProduct = null,
  onEditCancel = () => {}
}) {
  const [form, setForm] = useState(initialForm);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryError, setCategoryError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = (presetCategory = '') => {
    setForm({ ...initialForm, category: presetCategory });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get('http://localhost:5001/api/categories');
        setCategories(res.data);
        setCategoryError('');
      } catch (err) {
        console.error('Erreur lors du chargement des catégories :', err);
        setCategoryError("Impossible de charger les catégories");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fallbackCategory = categories[0]?.slug || '';
    if (editingProduct) {
      setForm({
        name: editingProduct.name || '',
        description: editingProduct.description || '',
        price: editingProduct.price ?? '',
        stock: editingProduct.stock ?? editingProduct.quantity ?? '',
        category: editingProduct.category || fallbackCategory,
        variety: editingProduct.variety || '',
        packQuantity: editingProduct.packQuantity !== undefined && editingProduct.packQuantity !== null
          ? editingProduct.packQuantity.toString()
          : '',
        volume: editingProduct.volume || '',
        alcoholPercentage: editingProduct.alcoholPercentage || '',
        countryCode: editingProduct.countryCode || '',
        barcode: editingProduct.barcode || '',
        weight: editingProduct.weight || '',
        pallet: editingProduct.pallet || '',
        image: null
      });
      setSuccess('');
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else if (categories.length) {
      setForm((prev) => ({
        ...prev,
        category: prev.category || fallbackCategory
      }));
    }
  }, [editingProduct, categories]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setForm({ ...form, image: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== '') data.append(key, value);
      });

      const isEditing = Boolean(editingProduct);
      const url = isEditing
        ? `http://localhost:5001/api/products/${editingProduct._id}`
        : 'http://localhost:5001/api/products';
      const method = isEditing ? 'put' : 'post';

      const response = await axios[method](url, data, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data'
        }
      });

      setError('');
      setSuccess(isEditing ? 'Produit mis à jour avec succès !' : 'Produit ajouté avec succès !');
      onProductAdded(response.data);
      resetForm(categories[0]?.slug || '');
      if (isEditing) {
        onEditCancel();
      }
    } catch (err) {
      console.error(err.response?.data || err.message);
      setSuccess('');
      const serverMessage = err.response?.data?.error;
      setError(serverMessage || "Erreur lors de l'ajout du produit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm(categories[0]?.slug || '');
    setSuccess('');
    setError('');
    onEditCancel();
  };

  const isEditing = Boolean(editingProduct);

  return (
    <form className={`admin-product-form ${className}`} onSubmit={handleSubmit} encType="multipart/form-data">
      <div className="admin-product-form__grid">
        {categoryError && (
          <p className="admin-product-form__message admin-product-form__message--error admin-product-form__message--full">{categoryError}</p>
        )}
        <div className="admin-product-form__field">
          <label htmlFor="product-name">Nom du produit</label>
          <input
            id="product-name"
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ex : Château Margaux 2015"
            required
          />
        </div>
        <div className="admin-product-form__field admin-product-form__field--full">
          <label htmlFor="product-description">Description</label>
          <textarea
            id="product-description"
            name="description"
            value={form.description}
            onChange={handleChange}
            rows="3"
            placeholder="Détails, notes de dégustation, format de la bouteille..."
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-price">Prix (EUR)</label>
          <input
            id="product-price"
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            min="0"
            step="0.01"
            required
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-stock">Quantité en stock</label>
          <input
            id="product-stock"
            type="number"
            name="stock"
            value={form.stock}
            onChange={handleChange}
            min="0"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-category">Catégorie</label>
          <select
            id="product-category"
            name="category"
            value={form.category}
            onChange={handleChange}
            required
            disabled={!categories.length}
          >
            {!categories.length && <option value="">Aucune catégorie</option>}
            {categories.map((cat) => (
              <option key={cat._id} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-variety">Variété</label>
          <input
            id="product-variety"
            type="text"
            name="variety"
            value={form.variety}
            onChange={handleChange}
            placeholder="Ex : Whisky (blended)"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-pack-quantity">Nombre de bouteilles / pack</label>
          <input
            id="product-pack-quantity"
            type="number"
            name="packQuantity"
            value={form.packQuantity}
            onChange={handleChange}
            min="0"
            step="1"
            placeholder="Ex : 6"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-volume">Volume</label>
          <input
            id="product-volume"
            type="text"
            name="volume"
            value={form.volume}
            onChange={handleChange}
            placeholder="Ex : 50.0 cl"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-alcohol">Pourcentage d'alcool</label>
          <input
            id="product-alcohol"
            type="text"
            name="alcoholPercentage"
            value={form.alcoholPercentage}
            onChange={handleChange}
            placeholder="Ex : 40.0 %"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-country">Code pays</label>
          <input
            id="product-country"
            type="text"
            name="countryCode"
            value={form.countryCode}
            onChange={handleChange}
            placeholder="Ex : GB"
            maxLength="5"
          />
        </div>
        <div className="admin-product-form__field admin-product-form__field--full">
          <label htmlFor="product-barcode">RÉFÉRENCE</label>
          <input
            id="product-barcode"
            type="text"
            name="barcode"
            value={form.barcode}
            onChange={handleChange}
            placeholder="Ex : REF-4969265728189"
          />
        </div>
        <div className="admin-product-form__field">
          <label htmlFor="product-weight">Poids</label>
          <input
            id="product-weight"
            type="text"
            name="weight"
            value={form.weight}
            onChange={handleChange}
            placeholder="Ex : 5.62 kg"
          />
        </div>
        <div className="admin-product-form__field admin-product-form__field--full">
          <label htmlFor="product-pallet">Informations palette</label>
          <input
            id="product-pallet"
            type="text"
            name="pallet"
            value={form.pallet}
            onChange={handleChange}
            placeholder="Ex : Pallet 133 cs / Layer 10 cs"
          />
        </div>
        <div className="admin-product-form__field admin-product-form__field--file">
          <label htmlFor="product-image">Visuel (image)</label>
          <input
            id="product-image"
            ref={fileInputRef}
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="admin-product-form__actions">
        {isEditing && (
          <button type="button" className="admin-product-form__cancel" onClick={handleCancel} disabled={isSubmitting}>
            Annuler
          </button>
        )}
        <button type="submit" className="admin-product-form__submit" disabled={isSubmitting}>
          {isEditing ? 'Mettre à jour' : 'Ajouter le produit'}
        </button>
      </div>
      {success && <p className="admin-product-form__message admin-product-form__message--success">{success}</p>}
      {error && <p className="admin-product-form__message admin-product-form__message--error">{error}</p>}
    </form>
  );
}

export default AdminProductForm;
